import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  FiArrowLeft,
  FiUser,
  FiBook,
  FiCalendar,
  FiDollarSign,
  FiEdit,
  FiMessageSquare,
  FiRefreshCw,
  FiTrendingUp,
  FiBell,
  FiFileText,
  FiCheck,
  FiX,
} from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Modal from '../../components/Modal/Modal'
import Button from '../../components/Button/Button'
import Select from '../../components/Select/Select'
import FormInput from '../../components/FormInput/FormInput'
import './EnrollmentDetail.css'

const EnrollmentDetail = () => {
  const { enrollmentId } = useParams()
  const navigate = useNavigate()

  const [enrollment, setEnrollment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [batches, setBatches] = useState([])
  const [payments, setPayments] = useState([])
  const [activeTab, setActiveTab] = useState('overview')

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  // Form states
  const [statusLoading, setStatusLoading] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [notes, setNotes] = useState('')
  const [notesLoading, setNotesLoading] = useState(false)
  const [transferBatchId, setTransferBatchId] = useState('')
  const [transferLoading, setTransferLoading] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState('')
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [reminderMessage, setReminderMessage] = useState('')
  const [reminderLoading, setReminderLoading] = useState(false)

  useEffect(() => {
    loadEnrollment()
    loadBatches()
  }, [enrollmentId])

  useEffect(() => {
    if (enrollment?.user?._id) {
      loadPayments()
    }
  }, [enrollment])

  const loadEnrollment = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminApi.get(`/enrollments/${enrollmentId}`)
      setEnrollment(response.data)
      setNewStatus(response.data.status || 'active')
      setNotes(response.data.notes || '')
      setPaymentStatus(response.data.paymentStatus || response.data.payment?.status || '')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load enrollment')
    } finally {
      setLoading(false)
    }
  }

  const loadBatches = async () => {
    try {
      const response = await adminApi.get('/batches', {
        params: { limit: 1000 },
      })
      const batchesList = response.data?.batches || response.data?.data || []
      setBatches(batchesList)
    } catch (err) {
      console.error('Failed to load batches:', err)
    }
  }

  const loadPayments = async () => {
    try {
      const response = await adminApi.get('/payments', {
        params: { userId: enrollment?.user?._id, limit: 100 },
      })
      const paymentsList = response.data?.payments || response.data?.data || []
      setPayments(paymentsList)
    } catch (err) {
      console.error('Failed to load payments:', err)
    }
  }

  const handleStatusUpdate = async () => {
    setStatusLoading(true)
    try {
      const response = await adminApi.patch(`/enrollments/${enrollmentId}`, {
        status: newStatus,
      })
      setEnrollment(response.data)
      setShowStatusModal(false)
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleApprove = async () => {
    setStatusLoading(true)
    try {
      await adminApi.patch(`/enrollments/${enrollmentId}`, {
        status: 'active',
      })
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve enrollment')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleReject = async () => {
    setStatusLoading(true)
    try {
      await adminApi.patch(`/enrollments/${enrollmentId}`, {
        status: 'dropped',
      })
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject enrollment')
    } finally {
      setStatusLoading(false)
    }
  }

  const handleNotesUpdate = async () => {
    setNotesLoading(true)
    try {
      const response = await adminApi.patch(`/enrollments/${enrollmentId}`, {
        notes,
      })
      setEnrollment(response.data)
      setShowNotesModal(false)
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update notes')
    } finally {
      setNotesLoading(false)
    }
  }

  const handleTransfer = async () => {
    if (!transferBatchId) {
      setError('Please select a batch to transfer to')
      return
    }
    setTransferLoading(true)
    try {
      await adminApi.patch(`/enrollments/${enrollmentId}`, {
        batch: transferBatchId,
      })
      setShowTransferModal(false)
      setTransferBatchId('')
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to transfer enrollment')
    } finally {
      setTransferLoading(false)
    }
  }

  const handlePaymentStatusUpdate = async () => {
    setPaymentLoading(true)
    try {
      await adminApi.patch(`/enrollments/${enrollmentId}`, {
        paymentStatus,
      })
      setShowPaymentModal(false)
      loadEnrollment()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payment status')
    } finally {
      setPaymentLoading(false)
    }
  }

  const handleSendReminder = async () => {
    setReminderLoading(true)
    try {
      // This would typically call a backend API to send reminder
      // For now, we'll just show a success message
      alert('Reminder sent successfully!')
      setShowReminderModal(false)
      setReminderMessage('')
    } catch (err) {
      setError('Failed to send reminder')
    } finally {
      setReminderLoading(false)
    }
  }

  if (loading) {
    return <div className="detail-loading">Loading enrollment details...</div>
  }

  if (error && !enrollment) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate('/enrollments')}>Back to Enrollments</Button>
      </div>
    )
  }

  return (
    <div className="enrollment-detail-page">
      <header className="detail-header">
        <Link to="/enrollments" className="back-link">
          <FiArrowLeft size={20} />
          Back to Enrollments
        </Link>
        <div className="header-actions">
          {enrollment?.status !== 'active' && (
            <Button variant="primary" onClick={handleApprove} loading={statusLoading}>
              <FiCheck size={18} />
              Approve
            </Button>
          )}
          {enrollment?.status !== 'dropped' && (
            <Button variant="danger" onClick={handleReject} loading={statusLoading}>
              <FiX size={18} />
              Reject
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowStatusModal(true)}>
            Update Status
          </Button>
        </div>
      </header>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="detail-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'notes' ? 'active' : ''}
          onClick={() => setActiveTab('notes')}
        >
          <FiMessageSquare size={16} />
          Notes
        </button>
        <button
          className={activeTab === 'payments' ? 'active' : ''}
          onClick={() => setActiveTab('payments')}
        >
          <FiDollarSign size={16} />
          Payments
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          <FiFileText size={16} />
          History
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          <FiTrendingUp size={16} />
          Analytics
        </button>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          {activeTab === 'overview' && (
            <>
              <section className="detail-card">
                <div className="card-header">
                  <h1>Enrollment Details</h1>
                  <StatusBadge status={enrollment?.status || 'active'} />
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <FiUser size={18} />
                    <div>
                      <span className="info-label">Student</span>
                      <Link
                        to={`/users/${enrollment?.user?._id}`}
                        className="info-link"
                      >
                        <strong>
                          {enrollment?.user?.firstName} {enrollment?.user?.lastName}
                        </strong>
                      </Link>
                      <span>{enrollment?.user?.email}</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiBook size={18} />
                    <div>
                      <span className="info-label">Batch</span>
                      <Link
                        to={`/batches/${enrollment?.batch?._id}`}
                        className="info-link"
                      >
                        <strong>{enrollment?.batch?.batchName || '—'}</strong>
                      </Link>
                      <span>
                        {enrollment?.batch?.course?.title || enrollment?.course?.title || '—'}
                      </span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiCalendar size={18} />
                    <div>
                      <span className="info-label">Enrolled On</span>
                      <strong>
                        {enrollment?.joinedAt || enrollment?.createdAt
                          ? new Date(
                              enrollment?.joinedAt || enrollment?.createdAt
                            ).toLocaleDateString()
                          : '—'}
                      </strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiDollarSign size={18} />
                    <div>
                      <span className="info-label">Payment Status</span>
                      <strong>
                        {enrollment?.paymentStatus ||
                          enrollment?.payment?.status ||
                          '—'}
                      </strong>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setShowPaymentModal(true)}
                        style={{ marginTop: 'var(--space-2)' }}
                      >
                        Update Payment
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

          {activeTab === 'notes' && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Notes & Comments</h2>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setNotes(enrollment?.notes || '')
                    setShowNotesModal(true)
                  }}
                >
                  <FiEdit size={18} />
                  {enrollment?.notes ? 'Edit Notes' : 'Add Notes'}
                </Button>
              </div>
              {enrollment?.notes ? (
                <div className="notes-content">
                  <p>{enrollment.notes}</p>
                  <span className="notes-date">
                    Last updated:{' '}
                    {enrollment?.updatedAt
                      ? new Date(enrollment.updatedAt).toLocaleString()
                      : '—'}
                  </span>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No notes added yet. Click "Add Notes" to add comments.</p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'payments' && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Payment History</h2>
                <Button
                  variant="secondary"
                  onClick={() => setShowPaymentModal(true)}
                >
                  Update Payment Status
                </Button>
              </div>
              {payments.length > 0 ? (
                <div className="payments-list">
                  {payments.map((payment) => (
                    <div key={payment._id} className="payment-item">
                      <div>
                        <strong>₹{payment.amount || 0}</strong>
                        <span className="payment-method">
                          {payment.paymentMethod || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <StatusBadge
                          status={payment.status === 'completed' ? 'active' : payment.status}
                        />
                        <span className="payment-date">
                          {payment.createdAt
                            ? new Date(payment.createdAt).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No payment records found.</p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'history' && (
            <section className="detail-card">
              <h2>Enrollment History</h2>
              <div className="history-list">
                <div className="history-item">
                  <div className="history-marker"></div>
                  <div className="history-content">
                    <strong>Enrollment Created</strong>
                    <p>
                      {enrollment?.createdAt
                        ? new Date(enrollment.createdAt).toLocaleString()
                        : '—'}
                    </p>
                  </div>
                </div>
                {enrollment?.joinedAt && (
                  <div className="history-item">
                    <div className="history-marker"></div>
                    <div className="history-content">
                      <strong>Student Joined</strong>
                      <p>{new Date(enrollment.joinedAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {enrollment?.updatedAt && (
                  <div className="history-item">
                    <div className="history-marker"></div>
                    <div className="history-content">
                      <strong>Last Updated</strong>
                      <p>{new Date(enrollment.updatedAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {enrollment?.status && (
                  <div className="history-item">
                    <div className="history-marker"></div>
                    <div className="history-content">
                      <strong>Current Status</strong>
                      <p>
                        <StatusBadge status={enrollment.status} />
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {activeTab === 'analytics' && (
            <section className="detail-card">
              <h2>Enrollment Analytics</h2>
              <div className="analytics-grid">
                <div className="analytics-item">
                  <span className="analytics-label">Enrollment Duration</span>
                  <strong className="analytics-value">
                    {enrollment?.joinedAt
                      ? Math.floor(
                          (new Date() - new Date(enrollment.joinedAt)) /
                            (1000 * 60 * 60 * 24)
                        )
                      : 0}{' '}
                    days
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Status</span>
                  <strong className="analytics-value">
                    <StatusBadge status={enrollment?.status || 'active'} />
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Payment Status</span>
                  <strong className="analytics-value">
                    {enrollment?.paymentStatus ||
                      enrollment?.payment?.status ||
                      'Pending'}
                  </strong>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowStatusModal(true)}
              >
                Change Status
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setNotes(enrollment?.notes || '')
                  setShowNotesModal(true)
                }}
              >
                <FiMessageSquare size={16} />
                {enrollment?.notes ? 'Edit Notes' : 'Add Notes'}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowTransferModal(true)}
              >
                <FiRefreshCw size={16} />
                Transfer Batch
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowPaymentModal(true)}
              >
                <FiDollarSign size={16} />
                Update Payment
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowReminderModal(true)}
              >
                <FiBell size={16} />
                Send Reminder
              </Button>
              <Link to={`/users/${enrollment?.user?._id}`}>
                <Button variant="secondary" fullWidth>
                  View Student Profile
                </Button>
              </Link>
              <Link to={`/batches/${enrollment?.batch?._id}`}>
                <Button variant="secondary" fullWidth>
                  View Batch Details
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Enrollment Status"
        size="small"
      >
        <Select
          label="Status"
          name="status"
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'completed', label: 'Completed' },
            { value: 'dropped', label: 'Dropped' },
          ]}
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleStatusUpdate} loading={statusLoading}>
            Update Status
          </Button>
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title={enrollment?.notes ? 'Edit Notes' : 'Add Notes'}
        size="medium"
      >
        <FormInput
          label="Notes"
          name="notes"
          type="textarea"
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes or comments about this enrollment..."
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleNotesUpdate} loading={notesLoading}>
            Save Notes
          </Button>
        </div>
      </Modal>

      {/* Transfer Modal */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false)
          setTransferBatchId('')
        }}
        title="Transfer Enrollment"
        size="medium"
      >
        <Select
          label="Select New Batch"
          name="batch"
          value={transferBatchId}
          onChange={(e) => setTransferBatchId(e.target.value)}
          options={[
            { value: '', label: 'Select a batch' },
            ...batches
              .filter((b) => b._id !== enrollment?.batch?._id)
              .filter((b) => b.status === 'upcoming' || b.status === 'ongoing')
              .map((b) => ({
                value: b._id,
                label: `${b.batchName} (${b.status})`,
              })),
          ]}
          required
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowTransferModal(false)
              setTransferBatchId('')
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleTransfer} loading={transferLoading}>
            Transfer
          </Button>
        </div>
      </Modal>

      {/* Payment Status Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Update Payment Status"
        size="small"
      >
        <Select
          label="Payment Status"
          name="paymentStatus"
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
            { value: 'refunded', label: 'Refunded' },
          ]}
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            Cancel
          </Button>
          <Button onClick={handlePaymentStatusUpdate} loading={paymentLoading}>
            Update Payment
          </Button>
        </div>
      </Modal>

      {/* Reminder Modal */}
      <Modal
        isOpen={showReminderModal}
        onClose={() => {
          setShowReminderModal(false)
          setReminderMessage('')
        }}
        title="Send Reminder"
        size="medium"
      >
        <FormInput
          label="Reminder Message"
          name="reminderMessage"
          type="textarea"
          rows={4}
          value={reminderMessage}
          onChange={(e) => setReminderMessage(e.target.value)}
          placeholder="Enter reminder message for the student..."
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowReminderModal(false)
              setReminderMessage('')
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSendReminder} loading={reminderLoading}>
            Send Reminder
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default EnrollmentDetail
